"use client";

import * as React from "react";

/**
 * Grab-and-drag scrolling for a horizontal scroller.
 *
 * The carousels in this product could only be moved by their arrow buttons or a
 * trackpad gesture; grabbing one with the mouse did nothing. Pointer events
 * cover mouse and pen in a single path. Touch is deliberately excluded — the
 * platform already scrolls these natively with momentum, and intercepting it
 * only makes that worse.
 *
 * Returns props to spread on the scroll container plus a `dragging` flag.
 *
 * Two details that are easy to get wrong, both learned the hard way:
 *
 *   - `scroll-snap-type: mandatory` fights a drag in progress, so the caller
 *     must drop snapping while `dragging` is true and restore it on release —
 *     that is what re-snaps the scroller to the nearest item on drop.
 *   - `click` fires AFTER `pointerup`, so the "was this a drag?" flag cannot
 *     live in the same ref that pointerup clears; otherwise a drag that ends
 *     over a card also activates that card.
 */
export function useDragScroll<T extends HTMLElement = HTMLDivElement>(
  ref: React.RefObject<T | null>,
  options: {
    /**
     * Settle onto a child element when the drag is released, instead of
     * leaving it to CSS snapping.
     *
     * CSS proximity snapping only engages if you happen to stop near a snap
     * point, so a slow half-drag just sat wherever it was left, and a quick
     * flick did nothing at all — neither reads as a carousel. With this on,
     * release always lands on a card: a flick advances one in the direction of
     * travel, a slow drag settles on whichever card is nearest.
     *
     * Pass a selector when only some children are snap targets.
     */
    snapToChildren?: boolean;
    childSelector?: string;
    /**
     * Leave this many pixels of the PREVIOUS item visible when settling, so the
     * carousel reads as a strip you are moving through rather than a series of
     * flush pages. The caller's own arrow handler must use the same offset or
     * the two will land in different places.
     */
    snapOffset?: number;
  } = {},
) {
  const { snapToChildren = false, childSelector, snapOffset = 0 } = options;
  const [dragging, setDragging] = React.useState(false);
  const dragRef = React.useRef<{ startX: number; startLeft: number } | null>(null);
  const movedRef = React.useRef(false);
  /** Last few pointer samples, for the release velocity. */
  const samplesRef = React.useRef<{ x: number; t: number }[]>([]);

  const onPointerDown = (e: React.PointerEvent<T>) => {
    if (e.pointerType === "touch" || e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    dragRef.current = { startX: e.clientX, startLeft: el.scrollLeft };
    movedRef.current = false;
    samplesRef.current = [{ x: e.clientX, t: performance.now() }];
    setDragging(true);
    /*
      Capture is NOT taken here.

      `setPointerCapture` retargets every subsequent pointer event to the
      scroller, and the browser then computes `click` from the pointerdown /
      pointerup targets — both of which have become the scroller. So a plain
      click on a card inside a draggable rail fired on the container and the
      link never activated: every card in the ej-tech trending strip, the
      ej-tech related rail and the LJ carousel was unclickable.

      Capture is taken in `onPointerMove` instead, once movement passes the
      threshold and the gesture is genuinely a drag. A click still needs it
      never to be taken at all.
    */
  };

  const onPointerMove = (e: React.PointerEvent<T>) => {
    const d = dragRef.current;
    const el = ref.current;
    if (!d || !el) return;
    const dx = e.clientX - d.startX;
    if (Math.abs(dx) > 3 && !movedRef.current) {
      movedRef.current = true;
      // Now it is a drag: take capture so the gesture survives leaving the
      // element, and so `endDrag` still fires if the pointer is released
      // outside it.
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        // Pointer already gone; the drag simply ends on the next event.
      }
    }
    el.scrollLeft = d.startLeft - dx;
    const s = samplesRef.current;
    s.push({ x: e.clientX, t: performance.now() });
    if (s.length > 5) s.shift();
  };

  const endDrag = (e: React.PointerEvent<T>) => {
    const el = ref.current;
    if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
    const startLeft = dragRef.current?.startLeft ?? null;
    const wasDragging = dragRef.current !== null;
    dragRef.current = null;
    setDragging(false);
    if (!el || !wasDragging || !snapToChildren || !movedRef.current) return;

    // Velocity over the last samples, px/ms. Positive means the pointer was
    // moving right, i.e. the content is travelling left → previous card.
    const s = samplesRef.current;
    const first = s[0];
    const last = s[s.length - 1];
    const dt = last && first ? last.t - first.t : 0;
    const velocity = dt > 0 ? (last.x - first.x) / dt : 0;
    samplesRef.current = [];

    const children = (
      childSelector
        ? [...el.querySelectorAll<HTMLElement>(childSelector)]
        : ([...el.children] as HTMLElement[])
    ).filter((c) => c.offsetWidth > 0);
    if (children.length === 0) return;

    const positions = children.map((c) => offsetWithin(c, el));
    // Compare against the RESTING position of each card, which is its offset
    // minus the peek — otherwise every index is read half a peek out.
    const nearestTo = (scrollLeft: number) => {
      let idx = 0;
      let best = Infinity;
      positions.forEach((pos, i) => {
        const d2 = Math.abs(pos - snapOffset - scrollLeft);
        if (d2 < best) {
          best = d2;
          idx = i;
        }
      });
      return idx;
    };
    const nearest = nearestTo(el.scrollLeft);

    /**
     * A flick advances one card even if the drag never reached half-way — but
     * ONLY when the drag is still sitting on the card it started from.
     *
     * Without that guard the bump compounded with the distance already
     * travelled: a deliberate drag from card 1 to card 2 landed nearest card 2
     * and then the release velocity pushed it on to card 3. QA: "when I drag at
     * 1973 and move to 1977, it will snap at 1988."
     */
    const FLICK = 0.35;
    const startIndex = startLeft === null ? nearest : nearestTo(startLeft);
    let target = nearest;
    if (nearest === startIndex) {
      if (velocity <= -FLICK) target = Math.min(children.length - 1, nearest + 1);
      else if (velocity >= FLICK) target = Math.max(0, nearest - 1);
    }

    const max = el.scrollWidth - el.clientWidth;
    el.scrollTo({
      left: Math.min(max, Math.max(0, positions[target] - snapOffset)),
      behavior: "smooth",
    });
  };

  const onClickCapture = (e: React.MouseEvent<T>) => {
    if (movedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      movedRef.current = false;
    }
  };

  return {
    dragging,
    dragProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
    /**
     * Cursor + snap classes; snapping is suspended for the drag's duration.
     *
     * `snap-proximity`, not `snap-mandatory`: mandatory forbids the scroller
     * from resting anywhere but a snap point, so every release lurched to the
     * nearest item and a short drag was pulled back to where it started.
     * Proximity engages only when you let go near a snap point.
     */
    dragClass: dragging
      ? "cursor-grabbing select-none"
      : snapToChildren
        ? // The release handler settles onto a card itself, so CSS snapping
          // would only fight it (and re-snap after our smooth scroll).
          "cursor-grab"
        : "cursor-grab snap-x snap-proximity",
  };
}

/**
 * Scroll offset of a child within its scroll container.
 *
 * NOT `offsetLeft`: that is measured from the nearest *positioned* ancestor,
 * which for these scrollers is usually some wrapper further up, so every value
 * carries a constant error. Snapping papered over it — the browser corrected
 * the wrong target to the nearest snap point — but any scroller without
 * mandatory snapping would land in the wrong place.
 */
export function offsetWithin(child: HTMLElement, scroller: HTMLElement) {
  return (
    child.getBoundingClientRect().left -
    scroller.getBoundingClientRect().left +
    scroller.scrollLeft
  );
}
